import { useState } from 'react';
import Icon from './Icon';
import { useApp } from '../lib/AppContext';

export const SHOPPING_GROUPS = [
  { key: 'protein', label: 'Đạm', color: 'text-protein' },
  { key: 'dairy_eggs', label: 'Sữa & trứng', color: 'text-[#8a6100]' },
  { key: 'carbs', label: 'Tinh bột', color: 'text-[#a4471b]' },
  { key: 'produce', label: 'Rau củ & trái cây', color: 'text-[#17804f]' },
  { key: 'other', label: 'Khác', color: 'text-muted' },
];

const shareText = (title, items) => [
  title,
  ...SHOPPING_GROUPS.flatMap((g) => {
    const rows = items.filter((i) => i.group === g.key);
    if (rows.length === 0) return [];
    return ['', g.label.toUpperCase(), ...rows.map((i) => `${i.checked ? '☑' : '☐'} ${i.name}${i.quantity ? ` — ${i.quantity}` : ''}`)];
  }),
].join('\n');

// The week's shopping list: progress, groups for the store, square checkboxes in 48px rows. Ticks are saved
// on the menu (shown at once, rolled back if the save fails). Every item should have a quantity.
export default function ShoppingList({ title, items, onToggle }) {
  const { editable } = useApp();
  const [pending, setPending] = useState({});
  const [shared, setShared] = useState(null);
  const shown = items.map((item, index) => ({ ...item, index, checked: pending[index] ?? item.checked }));
  const done = shown.filter((i) => i.checked).length;

  const toggle = async (index, checked) => {
    setPending((p) => ({ ...p, [index]: checked }));
    try {
      await onToggle(index, checked);
    } catch (e) {
      window.alert(e.message);
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[index];
        return next;
      });
    }
  };

  // The phone's share sheet (Zalo, Messages…); copy to the clipboard where there is none.
  const share = async () => {
    const text = shareText(title, shown);
    try {
      if (navigator.share) {
        await navigator.share({ title, text });
      } else {
        await navigator.clipboard.writeText(text);
        setShared('Đã chép danh sách');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') window.prompt('Sao chép danh sách này:', text);
    }
  };

  if (items.length === 0) {
    return <p className="rounded-[20px] bg-base-100 p-5 text-center text-sm text-muted">Thực đơn này chưa có danh sách đi chợ.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="tabular text-[15px] font-semibold">Đã mua {done}/{shown.length} món</div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-base-300" role="progressbar" aria-label="Đã mua"
            aria-valuemin={0} aria-valuemax={shown.length} aria-valuenow={done}>
            <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${(done / shown.length) * 100}%` }} />
          </div>
        </div>
        <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100 px-4 font-semibold" onClick={share}>
          <Icon name="shareNodes" className="size-5" /> Chia sẻ
        </button>
      </div>
      {shared && <p role="status" className="text-sm text-brand-text">{shared}</p>}

      {SHOPPING_GROUPS.map((group) => {
        const rows = shown.filter((i) => i.group === group.key);
        if (rows.length === 0) return null;
        return (
          <section key={group.key} className="rounded-[20px] bg-base-100 px-4 pb-1 pt-4">
            <h3 className={`text-xs font-bold uppercase tracking-wide ${group.color}`}>{group.label}</h3>
            <ul className="divide-y divide-line">
              {rows.map((item) => (
                <li key={item.index}>
                  <label className={`flex min-h-12 items-center gap-3 py-2 ${editable ? 'cursor-pointer' : ''}`}>
                    <input type="checkbox" className="checkbox checkbox-primary size-6 rounded-[6px]" checked={item.checked}
                      disabled={!editable} onChange={(e) => toggle(item.index, e.target.checked)} />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[15px] ${item.checked ? 'text-muted line-through' : ''}`}>{item.name}</span>
                      {item.detail && <span className="block line-clamp-2 text-xs text-muted">{item.detail}</span>}
                    </span>
                    {item.quantity ? (
                      <span className={`tabular shrink-0 text-sm ${item.checked ? 'text-muted' : 'font-bold'}`}>{item.quantity}</span>
                    ) : (
                      <span className="shrink-0 text-sm font-semibold text-warning">Chưa có số lượng</span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
